import { useState } from 'react'
import { useCommonCodes, useDongs } from '@/shared/lib/codes'
import { EMPTY_FILTER, filterCount, type FilterValue } from '@/shared/lib/filterValue'

const TARGET_LIFECYCLE = 'target_lifecycle'
const TARGET_SITUATION = 'target_situation'

export type FilterPick<T> = {
  categories: (item: T) => string[]
  targets: (item: T) => string[]
  regions: (item: T) => string[]
}

export function useListFilter<T>(
  items: T[],
  pick: FilterPick<T>,
  initialGroup: string | null = null,
  initialRegions: string[] = [],
) {
  const groups = useCommonCodes('category_group')
  const categories = useCommonCodes('category')
  const lifecycle = useCommonCodes(TARGET_LIFECYCLE)
  const situation = useCommonCodes(TARGET_SITUATION)
  const dongs = useDongs()

  const [group, setGroup] = useState<string | null>(initialRegions.length > 0 ? null : initialGroup)
  const [filter, setFilter] = useState<FilterValue>(
    initialRegions.length > 0 ? { ...EMPTY_FILTER, regions: initialRegions } : EMPTY_FILTER,
  )
  const [sheetOpen, setSheetOpen] = useState(false)
  const hasFilter = filterCount(filter) > 0

  const parentOf = new Map((categories.data ?? []).map((c) => [c.code, c.parentCode]))

  const byGroup =
    group === null || hasFilter
      ? items
      : items.filter((it) => pick.categories(it).some((c) => parentOf.get(c) === group))

  const filtered = byGroup.filter((it) => {
    const okCategory =
      filter.categories.length === 0 ||
      pick.categories(it).some((c) => filter.categories.includes(c))
    const okTarget =
      filter.targets.length === 0 || pick.targets(it).some((c) => filter.targets.includes(c))
    const okRegion =
      filter.regions.length === 0 || pick.regions(it).some((c) => filter.regions.includes(c))
    return okCategory && okTarget && okRegion
  })

  const selectGroup = (next: string | null) => {
    setGroup(next)
    setFilter(EMPTY_FILTER)
  }

  const applyFilter = (next: FilterValue) => {
    setFilter(next)
    if (filterCount(next) > 0) setGroup(null)
    setSheetOpen(false)
  }

  return {
    groups,
    categories,
    lifecycle,
    situation,
    dongs,
    group,
    filter,
    hasFilter,
    sheetOpen,
    setSheetOpen,
    selectGroup,
    applyFilter,
    filtered,
  }
}
